import {useCallback, useRef} from 'react';

import {OptionsType, composePath, useNavigate} from 'shared/web';

export type NavigateWithoutBlocker = {
  (delta: number): void;
  <Path extends string>(path: Path, options?: OptionsType<Path>): void;
};

export type WithoutBlocker = (callback: () => void) => void;

export function useNavigateWithoutBlocker() {
  const navigate = useNavigate();
  const shouldNavigateWithoutBlockerRef = useRef<boolean>(false);

  /**
   * Navigates to a route path or history delta while bypassing the UnsavedChangesBlocker.
   *
   * Use this when the form's onSubmit handler needs to navigate away from the page
   * and you want to skip the "unsaved changes" dialog.
   *
   * For non-navigate actions that still trigger React Router navigation
   * (e.g. setSearchParams, query-state tab switches), use {@link withoutBlocker} instead.
   *
   * @example
   * // Navigate to a route without triggering the blocker
   * navigateWithoutBlocker(settingsRoutes.myRoute);
   *
   * @example
   * // Go back in history without triggering the blocker
   * navigateWithoutBlocker(-1);
   */
  const navigateWithoutBlocker: NavigateWithoutBlocker = (
    pathOrDelta: string | number,
    options?: OptionsType<string>
  ) => {
    shouldNavigateWithoutBlockerRef.current = true;
    try {
      if (typeof pathOrDelta === 'number') {
        navigate(pathOrDelta);
      } else {
        navigate(composePath(pathOrDelta, options));
      }
    } finally {
      shouldNavigateWithoutBlockerRef.current = false;
    }
  };

  /**
   * Wraps a synchronous callback so that any React Router navigation triggered
   * inside it bypasses the UnsavedChangesBlocker.
   *
   * Use this for actions that cause navigation indirectly — such as updating
   * query params via `setSearchParams` or switching tabs with `useQueryState` —
   * where {@link navigateWithoutBlocker} does not apply because you are not
   * calling `navigate()` directly.
   *
   * @example
   * // Switch tabs without triggering the blocker
   * withoutBlocker(() => setActiveTabId('documents'));
   */
  const withoutBlocker: WithoutBlocker = useCallback((callback: () => void) => {
    shouldNavigateWithoutBlockerRef.current = true;
    try {
      callback();
    } finally {
      shouldNavigateWithoutBlockerRef.current = false;
    }
  }, []);

  return {navigateWithoutBlocker, withoutBlocker, shouldNavigateWithoutBlockerRef};
}
